import { queryOptions } from "@tanstack/react-query";
import { api, type Me } from "./api";

// --- Auth / current user --------------------------------------------------

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: () => api.get<Me>("/api/v1/me"),
});

// --- Game shapes ----------------------------------------------------------

export type GameStatus = "lobby" | "in_progress" | "finished";

export interface Game {
  id: string;
  status: GameStatus;
  currentRound: number;
  totalRounds: number;
  rules: { doubleBlankPenalty: boolean };
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  hostPlayerId: string;
}

export interface GamePlayer {
  id: string;
  name: string;
  isHost: boolean;
  joinedAt: string;
  userId: string | null;
}

export interface JoinTokenInfo {
  game: Game;
  alreadyJoined: boolean;
  /** Only set when alreadyJoined is true. */
  yourPlayerId?: string;
}

// --- Game queries ---------------------------------------------------------

export function gameQuery(id: string) {
  return queryOptions({
    queryKey: ["game", id] as const,
    queryFn: () => api.get<Game>(`/api/v1/games/${id}`),
    staleTime: 5_000,
  });
}

export function gamePlayersQuery(id: string) {
  return queryOptions({
    queryKey: ["game", id, "players"] as const,
    queryFn: () => api.get<GamePlayer[]>(`/api/v1/games/${id}/players`),
    staleTime: 5_000,
  });
}

export function joinTokenQuery(token: string) {
  return queryOptions({
    queryKey: ["join", token] as const,
    queryFn: () => api.get<JoinTokenInfo>(`/api/v1/join/${token}`),
    staleTime: 5_000,
  });
}

// --- Scoreboard / round shapes -------------------------------------------

export type RoundStatus = "current" | "past" | "future";

export interface ScoreboardPlayer {
  id: string;
  name: string;
  isHost: boolean;
  total: number;
}

export interface ScoreboardCell {
  playerId: string;
  scoreId: string | null;
  total: number | null;
  submitted: boolean;
  audited: boolean;
  hasPhoto: boolean;
}

export interface ScoreboardRound {
  number: number;
  engineDouble: number;
  status: RoundStatus;
  cells: ScoreboardCell[];
}

export interface Scoreboard {
  players: ScoreboardPlayer[];
  rounds: ScoreboardRound[];
  currentRound: number;
}

export function scoreboardQuery(id: string) {
  return queryOptions({
    queryKey: ["game", id, "scoreboard"] as const,
    queryFn: () => api.get<Scoreboard>(`/api/v1/games/${id}/scoreboard`),
    // Slice 5 will SSE-invalidate; until then poll-refresh is fast.
    staleTime: 2_000,
  });
}

/**
 * Per-round view of the scoreboard. Backed by the same scoreboard endpoint —
 * we don't ship a dedicated /rounds/:n GET. Both keys are invalidated as a
 * pair via the predicate-style invalidation in mutations.
 */
export interface RoundView {
  round: ScoreboardRound;
  players: ScoreboardPlayer[];
  currentRound: number;
  totalRounds: number;
}

// --- Finish-game recap ---------------------------------------------------

export interface FinishStanding {
  playerId: string;
  name: string;
  isHost: boolean;
  total: number;
  /** Cumulative gap to the rank-1 player (0 for the leader). */
  gapToLeader: number;
}

export interface FinishRoundCell {
  playerId: string;
  name: string;
  total: number;
  scoreId: string;
  hasPhoto: boolean;
}

export interface FinishRound {
  number: number;
  engineDouble: number;
  /** Backend-formatted intellectual-cowboy narration. Render verbatim. */
  story: string;
  cells: FinishRoundCell[];
  avg: number;
  low: number;
  high: number;
  stdDev: number;
}

export type FinishAwardId =
  | "conductor"
  | "locomotive"
  | "cool_hand"
  | "wheels_off"
  | "pacesetter"
  | "sharpshooter"
  | "comeback"
  | "caboose";

export interface FinishAward {
  id: FinishAwardId | string;
  label: string;
  icon: string;
  recipientPlayerId: string;
  recipientName: string;
  value: number;
  reason: string;
}

export interface FinishSummary {
  game: {
    id: string;
    totalRounds: number;
    finishedAt: string;
    rules: { doubleBlankPenalty: boolean };
  };
  standings: FinishStanding[];
  rounds: FinishRound[];
  awards: FinishAward[];
}

export function finishSummaryQuery(id: string) {
  return queryOptions({
    queryKey: ["game", id, "finish"] as const,
    queryFn: () => api.get<FinishSummary>(`/api/v1/games/${id}/finish`),
    // Immutable post-finish: cache long. SSE never publishes a finish-
    // mutating event, so we don't need predicate invalidation here.
    staleTime: 60_000,
  });
}

// --- Score detail / audit ------------------------------------------------

export interface ScoreDetailScore {
  id: string;
  total: number;
  roundNumber: number;
  engineDouble: number;
  hasPhoto: boolean;
  confirmedAt: string | null;
}

export interface ScoreDetailPlayer {
  id: string;
  name: string;
  isHost: boolean;
}

export interface ScoreAudit {
  id: string;
  auditorName: string;
  oldTotal: number;
  newTotal: number;
  note: string | null;
  createdAt: string;
}

export interface ScoreDetail {
  score: ScoreDetailScore;
  player: ScoreDetailPlayer;
  audits: ScoreAudit[];
}

export function scoreDetailQuery(gameId: string, scoreId: string) {
  return queryOptions({
    queryKey: ["game", gameId, "score", scoreId] as const,
    queryFn: () =>
      api.get<ScoreDetail>(`/api/v1/games/${gameId}/scores/${scoreId}`),
    staleTime: 1_000,
  });
}

export function roundQuery(id: string, n: number) {
  return queryOptions({
    queryKey: ["game", id, "round", n] as const,
    queryFn: async (): Promise<RoundView> => {
      const sb = await api.get<Scoreboard>(
        `/api/v1/games/${id}/scoreboard`,
      );
      const round = sb.rounds.find((r) => r.number === n);
      if (!round) {
        throw new Error(`round ${n} not found in scoreboard`);
      }
      return {
        round,
        players: sb.players,
        currentRound: sb.currentRound,
        totalRounds: sb.rounds.length,
      };
    },
    staleTime: 2_000,
  });
}
